/**
 * Duration Section - Business Value Calculator
 * Sprint 7: Day 2
 *
 * Features:
 * - 12-month allocation slider (Planning, Implementation, Optimization)
 * - Visual phase breakdown chart (Chart.js bar chart)
 * - Auto-validation (total must equal 12 months)
 * - Color-coded phases with gradients
 *
 * Phase Defaults (industry standard):
 * - Planning: 2 months
 * - Implementation: 8 months
 * - Optimization: 2 months
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface ProjectDuration {
  planningMonths: number;
  implementationMonths: number;
  optimizationMonths: number;
  totalMonths: number;
}

interface DurationSectionProps {
  duration: ProjectDuration;
  onDurationUpdate: (field: keyof ProjectDuration, value: number) => void;
}

export const DurationSection: React.FC<DurationSectionProps> = ({
  duration,
  onDurationUpdate
}) => {
  // Handle slider change with auto-balancing
  const handleSliderChange = (field: keyof ProjectDuration, value: string) => {
    const numericValue = parseInt(value) || 0;

    // Clamp to 0-12 range
    const clampedValue = Math.min(12, Math.max(0, numericValue));

    onDurationUpdate(field, clampedValue);
  };

  // Calculate if total is valid (should be 12)
  const totalMonths = duration.planningMonths + duration.implementationMonths + duration.optimizationMonths;
  const isValid = totalMonths === 12;
  const remaining = 12 - totalMonths;

  // Chart.js configuration
  const chartData = {
    labels: ['Planning', 'Implementation', 'Optimization'],
    datasets: [
      {
        label: 'Months',
        data: [
          duration.planningMonths,
          duration.implementationMonths,
          duration.optimizationMonths
        ],
        backgroundColor: [
          'rgba(0, 148, 171, 0.8)',  // Planning: Teal
          'rgba(0, 71, 114, 0.8)',   // Implementation: Dark blue
          'rgba(72, 187, 120, 0.8)'  // Optimization: Green
        ],
        borderColor: [
          'rgba(0, 148, 171, 1)',
          'rgba(0, 71, 114, 1)',
          'rgba(72, 187, 120, 1)'
        ],
        borderWidth: 2,
        borderRadius: 8
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Project Phase Breakdown (12 Months)',
        font: {
          size: 14,
          weight: 700
        },
        color: '#2d3748'
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 600
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context: { parsed: { y: number | null } }) {
            const value = context.parsed.y ?? 0;
            const percentage = ((value / 12) * 100).toFixed(0);
            return `${value} months (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 12,
        ticks: {
          stepSize: 2,
          callback: function(value: string | number) {
            return value + ' mo';
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            weight: 600
          }
        }
      }
    }
  };

  return (
    <div className="duration-section">
      <div className="section-help-text">
        <strong>Project Duration:</strong> Allocate your 12-month FAIR implementation across three phases.
        Industry standard: 2 months planning, 8 months implementation, 2 months optimization.
        Total must equal 12 months.
      </div>

      {/* Validation Message */}
      {!isValid && (
        <div className={`duration-alert ${remaining > 0 ? 'duration-alert-warning' : 'duration-alert-error'}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            {remaining > 0
              ? `You have ${remaining} month${remaining > 1 ? 's' : ''} remaining to allocate.`
              : `You've allocated ${Math.abs(remaining)} month${Math.abs(remaining) > 1 ? 's' : ''} too many. Total must be 12 months.`
            }
          </span>
        </div>
      )}

      <div className="duration-grid">
        {/* Left Column: Sliders */}
        <div className="duration-sliders">
          {/* Planning Phase */}
          <div className="duration-phase">
            <div className="duration-phase-header">
              <div className="duration-phase-label">
                <div className="duration-phase-color" style={{ background: '#0094ab' }} />
                <strong>Planning</strong>
              </div>
              <div className="duration-phase-value">
                <input
                  type="number"
                  min="0"
                  max="12"
                  value={duration.planningMonths}
                  onChange={(e) => handleSliderChange('planningMonths', e.target.value)}
                  className="duration-input"
                  aria-label="Planning months"
                />
                <span>months</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="12"
              step="1"
              value={duration.planningMonths}
              onChange={(e) => handleSliderChange('planningMonths', e.target.value)}
              className="duration-slider duration-slider-planning"
              aria-label="Planning phase duration"
            />
            <div className="duration-phase-description">
              Requirements gathering, architecture design, stakeholder alignment
            </div>
          </div>

          {/* Implementation Phase */}
          <div className="duration-phase">
            <div className="duration-phase-header">
              <div className="duration-phase-label">
                <div className="duration-phase-color" style={{ background: '#004772' }} />
                <strong>Implementation</strong>
              </div>
              <div className="duration-phase-value">
                <input
                  type="number"
                  min="0"
                  max="12"
                  value={duration.implementationMonths}
                  onChange={(e) => handleSliderChange('implementationMonths', e.target.value)}
                  className="duration-input"
                  aria-label="Implementation months"
                />
                <span>months</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="12"
              step="1"
              value={duration.implementationMonths}
              onChange={(e) => handleSliderChange('implementationMonths', e.target.value)}
              className="duration-slider duration-slider-implementation"
              aria-label="Implementation phase duration"
            />
            <div className="duration-phase-description">
              Development, testing, data migration, integration, training
            </div>
          </div>

          {/* Optimization Phase */}
          <div className="duration-phase">
            <div className="duration-phase-header">
              <div className="duration-phase-label">
                <div className="duration-phase-color" style={{ background: '#48bb78' }} />
                <strong>Optimization</strong>
              </div>
              <div className="duration-phase-value">
                <input
                  type="number"
                  min="0"
                  max="12"
                  value={duration.optimizationMonths}
                  onChange={(e) => handleSliderChange('optimizationMonths', e.target.value)}
                  className="duration-input"
                  aria-label="Optimization months"
                />
                <span>months</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="12"
              step="1"
              value={duration.optimizationMonths}
              onChange={(e) => handleSliderChange('optimizationMonths', e.target.value)}
              className="duration-slider duration-slider-optimization"
              aria-label="Optimization phase duration"
            />
            <div className="duration-phase-description">
              Performance tuning, user feedback, continuous improvement
            </div>
          </div>

          {/* Total Display */}
          <div className="duration-total">
            <span>Total:</span>
            <span className={`duration-total-value ${isValid ? 'duration-total-valid' : 'duration-total-invalid'}`}>
              {totalMonths} / 12 months
            </span>
          </div>
        </div>

        {/* Right Column: Chart */}
        <div className="duration-chart">
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>

      <style>{`
        .section-help-text {
          background: rgba(0, 148, 171, 0.05);
          border-left: 4px solid #0094ab;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.6;
        }

        .duration-alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .duration-alert-warning {
          background: #fef5e7;
          border: 1px solid #f9ca24;
          color: #b7791f;
        }

        .duration-alert-error {
          background: #fee;
          border: 1px solid #fc8181;
          color: #c53030;
        }

        .duration-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-top: 1.5rem;
        }

        .duration-sliders {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .duration-phase {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .duration-phase-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .duration-phase-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 700;
          color: #2d3748;
        }

        .duration-phase-color {
          width: 12px;
          height: 12px;
          border-radius: 3px;
        }

        .duration-phase-value {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #2d3748;
        }

        .duration-input {
          width: 50px;
          padding: 0.375rem 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          text-align: right;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .duration-input:focus {
          outline: none;
          border-color: #0094ab;
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.1);
        }

        .duration-slider {
          width: 100%;
          height: 8px;
          border-radius: 4px;
          outline: none;
          -webkit-appearance: none;
          appearance: none;
          cursor: pointer;
        }

        .duration-slider-planning {
          background: linear-gradient(to right, #e2e8f0 0%, #0094ab 100%);
        }

        .duration-slider-implementation {
          background: linear-gradient(to right, #e2e8f0 0%, #004772 100%);
        }

        .duration-slider-optimization {
          background: linear-gradient(to right, #e2e8f0 0%, #48bb78 100%);
        }

        .duration-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          border: 3px solid #0094ab;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .duration-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #ffffff;
          border: 3px solid #0094ab;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .duration-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 4px rgba(0, 148, 171, 0.3);
        }

        .duration-slider:focus::-moz-range-thumb {
          box-shadow: 0 0 0 4px rgba(0, 148, 171, 0.3);
        }

        .duration-phase-description {
          font-size: 0.75rem;
          color: #718096;
          line-height: 1.4;
        }

        .duration-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 1rem;
          border-top: 2px solid #e2e8f0;
          font-size: 1rem;
          font-weight: 700;
        }

        .duration-total-value {
          font-size: 1.25rem;
        }

        .duration-total-valid {
          color: #48bb78;
        }

        .duration-total-invalid {
          color: #fc8181;
        }

        .duration-chart {
          height: 400px;
          padding: 1rem;
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .duration-grid {
            grid-template-columns: 1fr;
          }

          .duration-chart {
            height: 300px;
          }
        }
      `}</style>
    </div>
  );
};

// Default project duration (industry standard)
export const DEFAULT_PROJECT_DURATION: ProjectDuration = {
  planningMonths: 2,
  implementationMonths: 8,
  optimizationMonths: 2,
  totalMonths: 12
};

export default DurationSection;
